import _ from "lodash";
import {
  CHART_SPREAD,
  CHART_CTT,
  CHART_BID_ASK_QUANTITY,
  GRANULARITY_DAY,
  GRANULARITY_WEEK,
  GRANULARITY_MONTH,
  GRANULARITY_QUARTER,
  GRANULARITY_YEAR,
  API_KEY_TIMESTAMP,
  API_KEYS_CTT,
  API_KEYS_SPREAD,
  API_KEYS_BID_ASK_QUANTITY,
  API_KEY_MID_PRICE,
  API_KEY_WEIGHTAGE,
  STORAGE_KEY_HOLIDAYS
} from "../managers/constants";
import datetimeManager from "../managers/datetime";
import chartMetaDataTransformer from "../transformers/chart-meta-data";
import webStorageManager from "../../services/managers/web-storage";

const service = (() => {
  // Do aggregation for various granularities
  const aggregate = (params) => {
    if (!params.rawData || !_.isArray(params.rawData) || !params.rawData.length) return [];

    let aggregatedData = [];
    let propertiesTobeNegatedForMirroring = []; // Used for Bid Ask Quantity Chart (Area Mirrored)
    let propertiesToRoundOffToOneDecimal = []; // Used for Bid Ask Quantity Chart (Area Mirrored)
    let propertiesForRecursiveAggregation = [];
    let rawDataLen = params.rawData.length; // Raw data coming in from the API
    let aggregationPropertiesLen = params.aggregationProperties.length; // Properties that require aggregation
    let isLastAggregatedObjectAveraged = true;
    let averagingDenominator = []; // Hold denominators (per aggregation property) for averaging calculation

    // Specifically for Bid Ask Quantity Chart (Area Mirrored)
    if (params.isNegatingRequiredForBidAskQuantityAreaMirroredChart) {
      propertiesTobeNegatedForMirroring = getPropertiesTobeNegatedForMirroring();
      propertiesForRecursiveAggregation = getPropertiesForRecursiveAggregation();
      propertiesToRoundOffToOneDecimal = _.pull(getAggregationProperties(CHART_BID_ASK_QUANTITY), API_KEY_MID_PRICE);
    }

    // Seflag value for exclude holidays filter
    const isExcludeHolidays = params.whiteboard.isExcludeHolidays;

    // Push the first object
    aggregatedData.push({ ...params.rawData[0] });
    doSelfWeightageMultiplicationAndDenominatorInitialization(aggregatedData[0], averagingDenominator, params.aggregationProperties);

    // Get list of holidays to be excluded
    const excludeDays = webStorageManager.get(STORAGE_KEY_HOLIDAYS);

    for (let index = 0; index < rawDataLen; index++) {
      // Skip the first object since it is already pushed to 'aggregatedData'
      if (index === 0) continue;

      // Skip the data for holiday
      if(isExcludeHolidays && excludeDays.includes(datetimeManager.getWholeDate(params.rawData[index][API_KEY_TIMESTAMP], params.offset, 'MM-DD-YYYY')))
      continue;

      if (
        chartMetaDataTransformer.checkIfWithinGranularityRange({
          fromTimestamp: aggregatedData[aggregatedData.length - 1][API_KEY_TIMESTAMP],
          toTimestamp: params.rawData[index][API_KEY_TIMESTAMP],
          granularity: params.granularity,
          offset: params.offset
        })
      ) {
        // Assign the new Timestamp to the aggregated object
        aggregatedData[aggregatedData.length - 1][API_KEY_TIMESTAMP] = params.rawData[index][API_KEY_TIMESTAMP];

        let newWeight = params.rawData[index][API_KEY_WEIGHTAGE];

        // Do summation for each property
        for (let propertyIndex = 0; propertyIndex < aggregationPropertiesLen; propertyIndex++) {
          let oldValue = aggregatedData[aggregatedData.length - 1][params.aggregationProperties[propertyIndex]];
          let newValue = params.rawData[index][params.aggregationProperties[propertyIndex]];

          if (_.isNumber(oldValue) && _.isNumber(newValue) && _.isNumber(newWeight) && newValue > 0) {
            aggregatedData[aggregatedData.length - 1][params.aggregationProperties[propertyIndex]] = oldValue + newValue * newWeight;
            averagingDenominator[params.aggregationProperties[propertyIndex]] =
              averagingDenominator[params.aggregationProperties[propertyIndex]] + newWeight;
          }
        }


      } else {
        let aggregatedDataLength = aggregatedData.length;

        // Calculate mid timestamp of granularity
        // Ex: For monthly, 15th of every month
        // Ex: For weekly, Thursday of every week
        aggregatedData[aggregatedDataLength - 1][API_KEY_TIMESTAMP] = calculateMidTimestampOfGranularity(
          params.granularity,
          aggregatedData[aggregatedDataLength - 1][API_KEY_TIMESTAMP],
          params.offset
        );

        doAveraging(
          aggregatedData[aggregatedDataLength - 1],
          averagingDenominator,
          params.aggregationProperties,
          params.isNegatingRequiredForBidAskQuantityAreaMirroredChart,
          propertiesTobeNegatedForMirroring,
          propertiesToRoundOffToOneDecimal,
          propertiesForRecursiveAggregation,
          params.whiteboard
        );

        aggregatedData.push({ ...params.rawData[index] });
        aggregatedDataLength++;
        averagingDenominator = [];
        doSelfWeightageMultiplicationAndDenominatorInitialization(
          aggregatedData[aggregatedDataLength - 1],
          averagingDenominator,
          params.aggregationProperties
        );
      }
      // Averaging the last object if it is single at the end
      if (index === rawDataLen - 1) isLastAggregatedObjectAveraged = false;
    }

    // Check the first element of the aggregated data array for holiday and remove it if it is holiday
    if(isExcludeHolidays && excludeDays.includes(datetimeManager.getWholeDate(aggregatedData[0][API_KEY_TIMESTAMP], params.offset, 'MM-DD-YYYY'))){
      aggregatedData.shift();
    }

    // For the last remaining object, if any
    if (!isLastAggregatedObjectAveraged) {
      let aggregatedDataLength = aggregatedData.length;

      // Calculate mid timestamp of granularity
      // Ex: For monthly, 15th of every month
      // Ex: For weekly, Thursday of every week
      aggregatedData[aggregatedDataLength - 1][API_KEY_TIMESTAMP] = calculateMidTimestampOfGranularity(
        params.granularity,
        aggregatedData[aggregatedDataLength - 1][API_KEY_TIMESTAMP],
        params.offset
      );

      doAveraging(
        aggregatedData[aggregatedDataLength - 1],
        averagingDenominator,
        params.aggregationProperties,
        params.isNegatingRequiredForBidAskQuantityAreaMirroredChart,
        propertiesTobeNegatedForMirroring,
        propertiesToRoundOffToOneDecimal,
        propertiesForRecursiveAggregation,
        params.whiteboard
      );
    }

    return aggregatedData;
  };

  const doSelfWeightageMultiplicationAndDenominatorInitialization = (dataObj, averagingDenominator, aggregationProperties) => {
    let aggregationPropertiesLen = aggregationProperties.length;
    for (let propertyIndex = 0; propertyIndex < aggregationPropertiesLen; propertyIndex++) {
      let value = dataObj[aggregationProperties[propertyIndex]];
      let weight = dataObj[API_KEY_WEIGHTAGE];
      if (_.isNumber(value) && _.isNumber(weight) && value > 0) {
        dataObj[aggregationProperties[propertyIndex]] = value * weight;
      }
      averagingDenominator[aggregationProperties[propertyIndex]] = _.isNumber(value) && value > 0 ? weight : 0;
    }
  };

  const doAveraging = (
    dataObj,
    averagingDenominator,
    aggregationProperties,
    isNegatingRequiredForBidAskQuantityAreaMirroredChart,
    propertiesTobeNegatedForMirroring,
    propertiesToRoundOffToOneDecimal,
    propertiesForRecursiveAggregation,
    chartWhiteboard
  ) => {

    // Aggregation for Bid Ask Quantity (Area Mirrored)
    if (chartWhiteboard.selectedPrimaryChart === CHART_BID_ASK_QUANTITY) {
      const propertiesForRecursiveAggregationLen = propertiesForRecursiveAggregation.length;
      for (let propertyIndex = 1; propertyIndex < propertiesForRecursiveAggregationLen; propertyIndex++) {
        dataObj[propertiesForRecursiveAggregation[propertyIndex]] = dataObj[propertiesForRecursiveAggregation[propertyIndex - 1]] + dataObj[propertiesForRecursiveAggregation[propertyIndex]];
      }

      const propertiesForNegativeRecursiveAggregationLen = propertiesTobeNegatedForMirroring.length;
      for (let propertyIndex = 1; propertyIndex < propertiesForNegativeRecursiveAggregationLen; propertyIndex++) {
        dataObj[propertiesTobeNegatedForMirroring[propertyIndex]] = dataObj[propertiesTobeNegatedForMirroring[propertyIndex - 1]] + dataObj[propertiesTobeNegatedForMirroring[propertyIndex]];
      }
    }

    let aggregationPropertiesLen = aggregationProperties.length;
    // Do averaging (divide each summed up property with 'averagingDenominator')
    for (let propertyIndex = 0; propertyIndex < aggregationPropertiesLen; propertyIndex++) {
      let numerator = dataObj[aggregationProperties[propertyIndex]];
      let denominator = averagingDenominator[aggregationProperties[propertyIndex]];

      if (_.isNumber(numerator) && _.isNumber(denominator) && denominator !== 0) {
        let average = numerator / denominator;

        // Round all values to 2 decimals
        average = _.round(average, 2);

        // Do negation for Bid Ask Quantity (Area Mirrored)
        if (
          isNegatingRequiredForBidAskQuantityAreaMirroredChart &&
          _.includes(propertiesTobeNegatedForMirroring, aggregationProperties[propertyIndex])
        ) {
          average = -average;
        }

        // Round to 1 decimal for Bid Ask Quantity
        if (
          isNegatingRequiredForBidAskQuantityAreaMirroredChart &&
          _.includes(propertiesToRoundOffToOneDecimal, aggregationProperties[propertyIndex])
        ) {
          average = _.round(average, 1);
        }

        // Round Mid Price to 5 decimals
        if (aggregationProperties[propertyIndex] === API_KEY_MID_PRICE) {
          average = _.round(average, 5);
        }

        dataObj[aggregationProperties[propertyIndex]] = average;
      }
    }
  };

  // Get aggregation properties (varying in JSONs) for different charts
  // Ex: L1B, L1S, L1BDep, L1SDep
  const getAggregationProperties = (selectedPrimaryChart, selectedComparisonChart, selectedLotSizes = []) => {
    let properties = [];

    if (
      (selectedPrimaryChart === CHART_CTT && selectedComparisonChart === CHART_SPREAD) ||
      (selectedPrimaryChart === CHART_SPREAD && selectedComparisonChart === CHART_CTT)
    ) {
      // Cost To Trade + Spread
      properties.push(API_KEYS_CTT.midPrice);
      _.forEach(selectedLotSizes, (lotSizeKey) => {
        properties = properties.concat([
          lotSizeKey + API_KEYS_CTT.bid,
          lotSizeKey + API_KEYS_CTT.ask,
          lotSizeKey + API_KEYS_CTT.bidDepth,
          lotSizeKey + API_KEYS_CTT.askDepth
        ]);
      });
      properties = properties.concat([
        API_KEYS_SPREAD.l1,
        API_KEYS_SPREAD.l2,
        API_KEYS_SPREAD.l3,
        API_KEYS_SPREAD.l4,
        API_KEYS_SPREAD.l5,
        API_KEYS_SPREAD.l6,
        API_KEYS_SPREAD.l7,
        API_KEYS_SPREAD.l8,
        API_KEYS_SPREAD.l9,
        API_KEYS_SPREAD.l10
      ]);
    } else if (
      (selectedPrimaryChart === CHART_CTT && selectedComparisonChart === CHART_BID_ASK_QUANTITY) ||
      (selectedPrimaryChart === CHART_BID_ASK_QUANTITY && selectedComparisonChart === CHART_CTT)
    ) {
      // Cost To Trade + Bid Ask Quantity
      properties.push(API_KEYS_CTT.midPrice);
      _.forEach(selectedLotSizes, (lotSizeKey) => {
        properties = properties.concat([
          lotSizeKey + API_KEYS_CTT.bid,
          lotSizeKey + API_KEYS_CTT.ask,
          lotSizeKey + API_KEYS_CTT.bidDepth,
          lotSizeKey + API_KEYS_CTT.askDepth
        ]);
      });
      properties = properties.concat([
        API_KEYS_BID_ASK_QUANTITY.l1Ask,
        API_KEYS_BID_ASK_QUANTITY.l2Ask,
        API_KEYS_BID_ASK_QUANTITY.l3Ask,
        API_KEYS_BID_ASK_QUANTITY.l4Ask,
        API_KEYS_BID_ASK_QUANTITY.l5Ask,
        API_KEYS_BID_ASK_QUANTITY.l6Ask,
        API_KEYS_BID_ASK_QUANTITY.l7Ask,
        API_KEYS_BID_ASK_QUANTITY.l8Ask,
        API_KEYS_BID_ASK_QUANTITY.l9Ask,
        API_KEYS_BID_ASK_QUANTITY.l10Ask,
        API_KEYS_BID_ASK_QUANTITY.l1Bid,
        API_KEYS_BID_ASK_QUANTITY.l2Bid,
        API_KEYS_BID_ASK_QUANTITY.l3Bid,
        API_KEYS_BID_ASK_QUANTITY.l4Bid,
        API_KEYS_BID_ASK_QUANTITY.l5Bid,
        API_KEYS_BID_ASK_QUANTITY.l6Bid,
        API_KEYS_BID_ASK_QUANTITY.l7Bid,
        API_KEYS_BID_ASK_QUANTITY.l8Bid,
        API_KEYS_BID_ASK_QUANTITY.l9Bid,
        API_KEYS_BID_ASK_QUANTITY.l10Bid
      ]);
    } else if (
      (selectedPrimaryChart === CHART_SPREAD && selectedComparisonChart === CHART_BID_ASK_QUANTITY) ||
      (selectedPrimaryChart === CHART_BID_ASK_QUANTITY && selectedComparisonChart === CHART_SPREAD)
    ) {
      // Spread + Bid Ask Quantity
      properties = [
        API_KEYS_SPREAD.midPrice,
        API_KEYS_SPREAD.l1,
        API_KEYS_SPREAD.l2,
        API_KEYS_SPREAD.l3,
        API_KEYS_SPREAD.l4,
        API_KEYS_SPREAD.l5,
        API_KEYS_SPREAD.l6,
        API_KEYS_SPREAD.l7,
        API_KEYS_SPREAD.l8,
        API_KEYS_SPREAD.l9,
        API_KEYS_SPREAD.l10
      ].concat([
        API_KEYS_BID_ASK_QUANTITY.l1Ask,
        API_KEYS_BID_ASK_QUANTITY.l2Ask,
        API_KEYS_BID_ASK_QUANTITY.l3Ask,
        API_KEYS_BID_ASK_QUANTITY.l4Ask,
        API_KEYS_BID_ASK_QUANTITY.l5Ask,
        API_KEYS_BID_ASK_QUANTITY.l6Ask,
        API_KEYS_BID_ASK_QUANTITY.l7Ask,
        API_KEYS_BID_ASK_QUANTITY.l8Ask,
        API_KEYS_BID_ASK_QUANTITY.l9Ask,
        API_KEYS_BID_ASK_QUANTITY.l10Ask,
        API_KEYS_BID_ASK_QUANTITY.l1Bid,
        API_KEYS_BID_ASK_QUANTITY.l2Bid,
        API_KEYS_BID_ASK_QUANTITY.l3Bid,
        API_KEYS_BID_ASK_QUANTITY.l4Bid,
        API_KEYS_BID_ASK_QUANTITY.l5Bid,
        API_KEYS_BID_ASK_QUANTITY.l6Bid,
        API_KEYS_BID_ASK_QUANTITY.l7Bid,
        API_KEYS_BID_ASK_QUANTITY.l8Bid,
        API_KEYS_BID_ASK_QUANTITY.l9Bid,
        API_KEYS_BID_ASK_QUANTITY.l10Bid
      ]);
    } else if (selectedPrimaryChart === CHART_CTT) {
      // Cost To Trade
      properties.push(API_KEYS_CTT.midPrice);
      _.forEach(selectedLotSizes, (lotSizeKey) => {
        properties = properties.concat([
          lotSizeKey + API_KEYS_CTT.bid,
          lotSizeKey + API_KEYS_CTT.ask,
          lotSizeKey + API_KEYS_CTT.bidDepth,
          lotSizeKey + API_KEYS_CTT.askDepth
        ]);
      });
    } else if (selectedPrimaryChart === CHART_SPREAD) {
      // Spread
      properties = [
        API_KEYS_SPREAD.midPrice,
        API_KEYS_SPREAD.l1,
        API_KEYS_SPREAD.l2,
        API_KEYS_SPREAD.l3,
        API_KEYS_SPREAD.l4,
        API_KEYS_SPREAD.l5,
        API_KEYS_SPREAD.l6,
        API_KEYS_SPREAD.l7,
        API_KEYS_SPREAD.l8,
        API_KEYS_SPREAD.l9,
        API_KEYS_SPREAD.l10
      ];
    } else if (selectedPrimaryChart === CHART_BID_ASK_QUANTITY) {
      // Bid Ask Quantity
      properties = [
        API_KEYS_BID_ASK_QUANTITY.midPrice,
        API_KEYS_BID_ASK_QUANTITY.l1Ask,
        API_KEYS_BID_ASK_QUANTITY.l2Ask,
        API_KEYS_BID_ASK_QUANTITY.l3Ask,
        API_KEYS_BID_ASK_QUANTITY.l4Ask,
        API_KEYS_BID_ASK_QUANTITY.l5Ask,
        API_KEYS_BID_ASK_QUANTITY.l6Ask,
        API_KEYS_BID_ASK_QUANTITY.l7Ask,
        API_KEYS_BID_ASK_QUANTITY.l8Ask,
        API_KEYS_BID_ASK_QUANTITY.l9Ask,
        API_KEYS_BID_ASK_QUANTITY.l10Ask,
        API_KEYS_BID_ASK_QUANTITY.l1Bid,
        API_KEYS_BID_ASK_QUANTITY.l2Bid,
        API_KEYS_BID_ASK_QUANTITY.l3Bid,
        API_KEYS_BID_ASK_QUANTITY.l4Bid,
        API_KEYS_BID_ASK_QUANTITY.l5Bid,
        API_KEYS_BID_ASK_QUANTITY.l6Bid,
        API_KEYS_BID_ASK_QUANTITY.l7Bid,
        API_KEYS_BID_ASK_QUANTITY.l8Bid,
        API_KEYS_BID_ASK_QUANTITY.l9Bid,
        API_KEYS_BID_ASK_QUANTITY.l10Bid
      ];
    }

    return properties;
  };

  // Used specifically for Bid Ask Quantity (Area Mirrored)
  const getPropertiesTobeNegatedForMirroring = () => [
    API_KEYS_BID_ASK_QUANTITY.l1Bid,
    API_KEYS_BID_ASK_QUANTITY.l2Bid,
    API_KEYS_BID_ASK_QUANTITY.l3Bid,
    API_KEYS_BID_ASK_QUANTITY.l4Bid,
    API_KEYS_BID_ASK_QUANTITY.l5Bid,
    API_KEYS_BID_ASK_QUANTITY.l6Bid,
    API_KEYS_BID_ASK_QUANTITY.l7Bid,
    API_KEYS_BID_ASK_QUANTITY.l8Bid,
    API_KEYS_BID_ASK_QUANTITY.l9Bid,
    API_KEYS_BID_ASK_QUANTITY.l10Bid
  ];

  // Used specifically for Bid Ask Quantity (Area Mirrored)
  const getPropertiesForRecursiveAggregation = () => [
    API_KEYS_BID_ASK_QUANTITY.l1Ask,
    API_KEYS_BID_ASK_QUANTITY.l2Ask,
    API_KEYS_BID_ASK_QUANTITY.l3Ask,
    API_KEYS_BID_ASK_QUANTITY.l4Ask,
    API_KEYS_BID_ASK_QUANTITY.l5Ask,
    API_KEYS_BID_ASK_QUANTITY.l6Ask,
    API_KEYS_BID_ASK_QUANTITY.l7Ask,
    API_KEYS_BID_ASK_QUANTITY.l8Ask,
    API_KEYS_BID_ASK_QUANTITY.l9Ask,
    API_KEYS_BID_ASK_QUANTITY.l10Ask
  ];

  const calculateMidTimestampOfGranularity = (granularity, rawDataTimestamp, offset) => {
    let convertedDate = datetimeManager.getMomentObject(rawDataTimestamp, offset);

    switch (granularity) {
      case GRANULARITY_DAY:
        return convertedDate.set({ hour: 12, minute: 0, second: 0 }).unix();
      case GRANULARITY_WEEK:
        return convertedDate
          .isoWeekday(4)
          .set({ hour: 12, minute: 0, second: 0 })
          .unix();
      case GRANULARITY_MONTH:
        return convertedDate.set({ date: 15, hour: 12, minute: 0, second: 0 }).unix();
      case GRANULARITY_QUARTER:
        let month = null;
        let quarter = datetimeManager.getQuarter(rawDataTimestamp, offset);
        if (quarter === 1) {
          month = 1; // Feb
        } else if (quarter === 2) {
          month = 4; // May
        } else if (quarter === 3) {
          month = 7; // Aug
        } else {
          month = 10; // Nov
        }
        return convertedDate
          .set({
            month: month,
            date: 15,
            hour: 12,
            minute: 0,
            second: 0
          })
          .unix();
      case GRANULARITY_YEAR:
        return convertedDate.set({ month: 5, date: 30, hour: 12, minute: 0, second: 0 }).unix();
      default:
        return;
    }
  };

  const mergeOverlayComparisonData = (primaryData, comparisonData) => {
    let len = primaryData.length;
    let mergedData = [];
    for (let index = 0; index < len; index++) {
      if (_.isObject(primaryData[index]) && _.isObject(comparisonData[index])) {
        mergedData.push(Object.assign({}, primaryData[index], comparisonData[index]));
      }
    }
    return mergedData;
  };

  return { aggregate, getAggregationProperties, mergeOverlayComparisonData };
})();

export default service;


// WEBPACK FOOTER //
// ./src/services/transformers/chart-data.js